import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Modal,
  Slider,
  SliderProps,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';

import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

import { useCustomSpotsEditor } from '@core/dashboard/components/DashboardContent/CustomSpotsEditor/useCustomSpotsEditor';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  ISpotLocalizationsMap,
} from '@services/BrandingsService';

import { getSpotType } from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useDrift } from '../DriftContext';
import {
  LocalizationSection,
  TOTAL_LOCALIZATION_LANGUAGES,
  useSpotLocalizations,
  useSpotLocalizationUploader,
} from './localization';
import { useLocalizationStyles } from './localization/styles';
import { useInspectorStyles } from './styles';

type LightboxMode = 'video' | 'image' | null;

type AnySpot = Record<string, unknown> & {
  brandingSpotId?: string;
  roomSpotId?: string;
  ueIdentifier?: string;
  name?: string;
  type?: string;
  isActive?: boolean;
  previewImage?: string;
  distance?: number | null;
  volume?: number;
  isMuted?: boolean;
  enableFullscreen?: boolean;
  data?: {
    url?: string;
    location?: string;
    previewImage?: string;
  };
  localizations?: ISpotLocalizationsMap;
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const UE_UNITS_PER_METER = 100;
const DISTANCE_MIN_METERS = 5;
const DISTANCE_MAX_METERS = 20;
const DISTANCE_DEFAULT_METERS = 5;
const DISTANCE_SAVE_DEBOUNCE_MS = 400;

const AUDIO_DEFAULT_VOLUME = 1;
const AUDIO_VOLUME_STEP = 0.05;
const AUDIO_SAVE_DEBOUNCE_MS = 400;

const normalizeVolume = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? value
    : AUDIO_DEFAULT_VOLUME;

const toVolumePercent = (volume: number): number => Math.round(volume * 100);

const ueUnitsToMeters = (ueUnits: unknown): number | null => {
  if (typeof ueUnits !== 'number' || !Number.isFinite(ueUnits)) {
    return null;
  }
  const meters = Math.round(ueUnits / UE_UNITS_PER_METER);
  if (meters < DISTANCE_MIN_METERS) {
    return DISTANCE_MIN_METERS;
  }
  if (meters > DISTANCE_MAX_METERS) {
    return DISTANCE_MAX_METERS;
  }
  return meters;
};

const metersToUeUnits = (meters: number): number => meters * UE_UNITS_PER_METER;

const getFilename = (url: string | null): string => {
  if (!url) {
    return '';
  }
  try {
    const u = new URL(url, window.location.href);
    const last = u.pathname.split('/').pop() || '';
    return decodeURIComponent(last);
  } catch {
    const last = url.split('/').pop() || '';
    return decodeURIComponent(last);
  }
};

interface SpotInspectorProps {
  ueIdentifier: string;
}

const DEFAULT_LANGUAGE_CODE = 'en';

const driftPanelStyles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    padding: '4px 2px 8px',
  },
  bannerMissing: {
    display: 'flex',
    gap: '9px',
    padding: '11px 12px',
    borderRadius: '11px',
    backgroundColor: 'rgba(245, 166, 35, 0.14)',
    color: '#f6d49a',
  },
  bannerOrphan: {
    display: 'flex',
    gap: '9px',
    padding: '11px 12px',
    borderRadius: '11px',
    backgroundColor: 'rgba(255, 77, 77, 0.14)',
    color: '#ffb0b0',
  },
  bannerIcon: {
    fontSize: 16,
    flexShrink: 0,
    marginTop: '1px',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    border: 'none',
    borderRadius: '11px',
    padding: '11px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: '#f5a623',
    color: '#231905',
    '&:hover': { filter: 'brightness(1.05)' },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  removeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    borderRadius: '11px',
    padding: '11px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: '#ff8585',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
};

// Matches HotspotInspector's deleteButtonStyles for a consistent destructive-action
// look across Inspector footers.
const deleteSpotButtonStyles = {
  textTransform: 'none',
  color: '#ff8585',
  borderColor: 'rgba(255, 255, 255, 0.18)',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  padding: '8px 14px',
  gap: '6px',
  '&:hover': {
    borderColor: '#FF6B6B',
    backgroundColor: 'rgba(255, 107, 107, 0.06)',
  },
};

const SpotInspector = ({ ueIdentifier }: SpotInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const localizationStyles = useLocalizationStyles();
  const { setAutosaveStatus, clearSelection, setSpotMediaUploadLocked } =
    useEditorMode();
  const { spotDrift, refreshSpots } = useDrift();
  const { selectedLanguage } = useMytaverse();

  const normalizedActiveLanguage = (
    selectedLanguage || DEFAULT_LANGUAGE_CODE
  ).toLowerCase();
  const isDefaultLanguageActive =
    normalizedActiveLanguage === DEFAULT_LANGUAGE_CODE;

  const {
    roomSpots,
    brandingSpots,
    currentBrandingId,
    isBrandingModeEnabled,
    selectedCustomizationSpotType,
    setSelectedCustomizationSpotType,
  } = useMytaverseEvent();

  const spot = useMemo<AnySpot | null>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    if (!Array.isArray(source)) {
      return null;
    }
    const selectedUeId = normalizeUeIdentifier(ueIdentifier);
    return (
      (source as AnySpot[]).find(
        (s) => normalizeUeIdentifier(s?.ueIdentifier) === selectedUeId,
      ) ?? null
    );
  }, [
    ueIdentifier,
    currentBrandingId,
    isBrandingModeEnabled,
    brandingSpots,
    roomSpots,
  ]);

  const rawSpotType =
    typeof spot?.type === 'string' ? spot.type.toUpperCase() : '';
  const isVideoStatic =
    selectedCustomizationSpotType === 'VIDEO_STATIC' ||
    rawSpotType === 'VIDEO_STATIC';
  const declaredType = getSpotType(spot ?? {});
  const isVideo =
    isVideoStatic ||
    (selectedCustomizationSpotType || declaredType) === 'VIDEO';

  // `selectedCustomizationSpotType` is global and outlives spot selection, so
  // it goes stale when a spot is selected via a path that doesn't set it
  // (e.g. an in-world CUSTOMISATION_ITEM_SELECTED click); resync it here to
  // the newly-selected spot's real type, same as the distance/volume resets below.
  const persistedSpotType =
    rawSpotType === 'VIDEO_STATIC' ? 'VIDEO_STATIC' : declaredType;
  useEffect(() => {
    if (!spot) {
      return;
    }
    setSelectedCustomizationSpotType(persistedSpotType);
  }, [ueIdentifier, persistedSpotType, spot, setSelectedCustomizationSpotType]);

  const controller = useCustomSpotsEditor({
    onClose: () => undefined,
    suppressAssetUpdatedModal: true,
  });

  const {
    isUploading,
    isApplyingToSurface,
    uploadProgress,
    uploadStepMessage,
    fileInputRef,
    videoFileInputRef,
    previewImageFileInputRef,
    handleImageFiles,
    handleVideoFileSelect,
    handlePreviewImageFileSelect,
    displayedPreviewUrl,
    selectedFile,
    selectedVideoFile,
    selectedVideoLocalUrl,
    videoPreviewImageFile,
    videoPreviewLocalUrl,
    isImageReadyToUpload,
    handleConfirmImage,
    handleConfirmVideoOnly,
    cancelVideoUpload,
    videoUploadCancellable,
    handleConfirmVideoPreviewOnly,
    handleRemoveVideoPreview,
    saveSpotDistance,
    saveSpotAudio,
    saveSpotEnableFullscreen,
    deleteSpot,
    clearSpotMedia,
    error,
  } = controller;

  const confirmedImageRef = useRef<File | null>(null);
  useEffect(() => {
    if (
      !isVideo &&
      selectedFile &&
      selectedFile !== confirmedImageRef.current &&
      isImageReadyToUpload
    ) {
      confirmedImageRef.current = selectedFile;
      setActiveUpload('image');
      handleConfirmImage();
    }
  }, [isVideo, selectedFile, isImageReadyToUpload, handleConfirmImage]);

  const isBusy = isUploading || isApplyingToSurface;

  const [activeUpload, setActiveUpload] = useState<
    'video' | 'preview' | 'image' | null
  >(null);

  const confirmedVideoRef = useRef<File | null>(null);
  useEffect(() => {
    if (
      isVideo &&
      selectedVideoFile &&
      selectedVideoFile !== confirmedVideoRef.current &&
      !isBusy
    ) {
      confirmedVideoRef.current = selectedVideoFile;
      setActiveUpload('video');
      handleConfirmVideoOnly();
    }
  }, [isVideo, selectedVideoFile, isBusy, handleConfirmVideoOnly]);

  const confirmedPreviewRef = useRef<File | null>(null);
  useEffect(() => {
    if (
      isVideo &&
      videoPreviewImageFile &&
      videoPreviewImageFile !== confirmedPreviewRef.current &&
      !isBusy
    ) {
      confirmedPreviewRef.current = videoPreviewImageFile;
      setActiveUpload('preview');
      handleConfirmVideoPreviewOnly();
    }
  }, [isVideo, videoPreviewImageFile, isBusy, handleConfirmVideoPreviewOnly]);

  const handleDeletePreview = useCallback(() => {
    setActiveUpload(null);
    handleRemoveVideoPreview();
  }, [handleRemoveVideoPreview]);

  const prevBusyRef = useRef(false);
  useEffect(() => {
    const busy = isUploading || isApplyingToSurface;
    if (busy && !prevBusyRef.current) {
      setAutosaveStatus('saving');
    } else if (!busy && prevBusyRef.current) {
      setAutosaveStatus(error ? 'error' : 'saved');
    }
    prevBusyRef.current = busy;
  }, [isUploading, isApplyingToSurface, error, setAutosaveStatus]);

  // Blocks the tree from selecting a different node while this spot's media
  // is mid-upload, so navigating away can't abandon the in-flight upload's UI.
  useEffect(() => {
    setSpotMediaUploadLocked(isBusy);
  }, [isBusy, setSpotMediaUploadLocked]);

  // Guards against leaving the whole tree locked if this inspector unmounts
  // mid-upload (e.g. the spot was deleted elsewhere, or the branding switched).
  useEffect(() => {
    return () => {
      setSpotMediaUploadLocked(false);
    };
  }, [setSpotMediaUploadLocked]);

  const englishOverride = spot?.localizations?.[DEFAULT_LANGUAGE_CODE] ?? null;
  const currentUrl =
    englishOverride?.url ||
    englishOverride?.location ||
    spot?.data?.url ||
    spot?.data?.location ||
    null;
  const previewImageUrl =
    englishOverride?.previewImage ||
    spot?.data?.previewImage ||
    spot?.previewImage ||
    null;
  const filename = getFilename(currentUrl);

  const isVideoSpot = declaredType === 'VIDEO' || isVideoStatic;
  const imageThumbUrl = isVideoSpot ? null : currentUrl;
  const persistedVideoUrl = isVideoSpot ? currentUrl : null;

  const previewThumb = isVideo ? videoPreviewLocalUrl || previewImageUrl : null;
  const imageOnlyThumb = !isVideo
    ? selectedFile
      ? displayedPreviewUrl
      : imageThumbUrl
    : null;

  const videoSrc = isVideo ? selectedVideoLocalUrl || persistedVideoUrl : null;

  const [lightboxMode, setLightboxMode] = useState<LightboxMode>(null);
  const [lightboxOverrideSrc, setLightboxOverrideSrc] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (isUploading || isApplyingToSurface) {
      setLightboxMode(null);
    }
  }, [isUploading, isApplyingToSurface]);

  useEffect(() => {
    if (lightboxMode === null) {
      setLightboxOverrideSrc(null);
    }
  }, [lightboxMode]);

  const brandingSpotId = spot?.brandingSpotId ?? null;
  const persistedDistanceMeters = ueUnitsToMeters(spot?.distance);
  const isDistanceExplicitlyInfinite = spot?.distance === null;

  const [distanceMeters, setDistanceMeters] = useState<number>(
    persistedDistanceMeters ?? DISTANCE_DEFAULT_METERS,
  );
  const [isDistanceInfinite, setIsDistanceInfinite] = useState<boolean>(
    isDistanceExplicitlyInfinite,
  );

  useEffect(() => {
    const next = ueUnitsToMeters(spot?.distance);
    setDistanceMeters(next ?? DISTANCE_DEFAULT_METERS);
    setIsDistanceInfinite(spot?.distance === null);
  }, [brandingSpotId, spot?.distance]);

  const distanceSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastSavedDistanceUeRef = useRef<number | null>(
    typeof spot?.distance === 'number' ? spot.distance : null,
  );

  useEffect(() => {
    lastSavedDistanceUeRef.current =
      typeof spot?.distance === 'number' ? spot.distance : null;
  }, [brandingSpotId, spot?.distance]);

  useEffect(() => {
    return () => {
      if (distanceSaveTimerRef.current) {
        clearTimeout(distanceSaveTimerRef.current);
      }
    };
  }, []);

  const persistDistance = useCallback(
    (nextUeUnits: number | null) => {
      if (!brandingSpotId) {
        return;
      }

      if (lastSavedDistanceUeRef.current === nextUeUnits) {
        return;
      }

      lastSavedDistanceUeRef.current = nextUeUnits;
      setAutosaveStatus('saving');

      saveSpotDistance(nextUeUnits)
        .then(() => {
          setAutosaveStatus('saved');
        })
        .catch((saveError: Error) => {
          console.error(`Failed to save spot distance: ${saveError?.message}`, {
            brandingSpotId,
          });
          setAutosaveStatus('error');
        });
    },
    [brandingSpotId, saveSpotDistance, setAutosaveStatus],
  );

  const scheduleDistanceSave = useCallback(
    (nextUeUnits: number | null) => {
      if (distanceSaveTimerRef.current) {
        clearTimeout(distanceSaveTimerRef.current);
      }
      distanceSaveTimerRef.current = setTimeout(() => {
        distanceSaveTimerRef.current = null;
        persistDistance(nextUeUnits);
      }, DISTANCE_SAVE_DEBOUNCE_MS);
    },
    [persistDistance],
  );

  const handleDistanceChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const meters = Array.isArray(value) ? value[0] : value;
      setDistanceMeters(meters);
      if (!isDistanceInfinite) {
        scheduleDistanceSave(metersToUeUnits(meters));
      }
    },
    [isDistanceInfinite, scheduleDistanceSave],
  );

  const handleToggleInfiniteDistance = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsDistanceInfinite(checked);
      if (checked) {
        scheduleDistanceSave(null);
      } else {
        scheduleDistanceSave(metersToUeUnits(distanceMeters));
      }
    },
    [distanceMeters, scheduleDistanceSave],
  );

  const showDistanceSection = isVideo && !!brandingSpotId;

  useEffect(() => {
    if (showDistanceSection && spot?.distance === undefined) {
      persistDistance(metersToUeUnits(DISTANCE_DEFAULT_METERS));
    }
  }, [showDistanceSection, spot?.distance, persistDistance]);

  const showAudioSection = rawSpotType === 'VIDEO' && !!brandingSpotId;

  const persistedVolume = normalizeVolume(spot?.volume);
  const persistedIsMuted = spot?.isMuted === true;

  const [volume, setVolume] = useState<number>(persistedVolume);
  const [isMuted, setIsMuted] = useState<boolean>(persistedIsMuted);

  useEffect(() => {
    setVolume(normalizeVolume(spot?.volume));
    setIsMuted(spot?.isMuted === true);
  }, [brandingSpotId, spot?.volume, spot?.isMuted]);

  const audioSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedAudioRef = useRef<{ volume: number; isMuted: boolean }>({
    volume: persistedVolume,
    isMuted: persistedIsMuted,
  });

  useEffect(() => {
    lastSavedAudioRef.current = {
      volume: normalizeVolume(spot?.volume),
      isMuted: spot?.isMuted === true,
    };
  }, [brandingSpotId, spot?.volume, spot?.isMuted]);

  useEffect(() => {
    return () => {
      if (audioSaveTimerRef.current) {
        clearTimeout(audioSaveTimerRef.current);
      }
    };
  }, []);

  const persistAudio = useCallback(
    (nextVolume: number, nextIsMuted: boolean) => {
      if (!brandingSpotId) {
        return;
      }

      const lastSaved = lastSavedAudioRef.current;
      if (
        lastSaved.volume === nextVolume &&
        lastSaved.isMuted === nextIsMuted
      ) {
        return;
      }

      lastSavedAudioRef.current = { volume: nextVolume, isMuted: nextIsMuted };
      setAutosaveStatus('saving');

      saveSpotAudio(nextVolume, nextIsMuted)
        .then(() => {
          setAutosaveStatus('saved');
        })
        .catch((saveError: Error) => {
          console.error(`Failed to save spot audio: ${saveError?.message}`, {
            brandingSpotId,
          });
          setAutosaveStatus('error');
        });
    },
    [brandingSpotId, saveSpotAudio, setAutosaveStatus],
  );

  const scheduleAudioSave = useCallback(
    (nextVolume: number, nextIsMuted: boolean) => {
      if (audioSaveTimerRef.current) {
        clearTimeout(audioSaveTimerRef.current);
      }
      audioSaveTimerRef.current = setTimeout(() => {
        audioSaveTimerRef.current = null;
        persistAudio(nextVolume, nextIsMuted);
      }, AUDIO_SAVE_DEBOUNCE_MS);
    },
    [persistAudio],
  );

  const handleVolumeChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const nextVolume = Array.isArray(value) ? value[0] : value;
      setVolume(nextVolume);
      scheduleAudioSave(nextVolume, isMuted);
    },
    [isMuted, scheduleAudioSave],
  );

  const handleToggleMute = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsMuted(checked);
      scheduleAudioSave(volume, checked);
    },
    [volume, scheduleAudioSave],
  );

  // Every spot type opens in the overlay, image included, so unlike the distance
  // and audio sections this one is not gated on `isVideo`. An absent flag means
  // the record predates MV-2071 and must still offer the click.
  const showFullscreenSection = !!brandingSpotId;
  const persistedEnableFullscreen = spot?.enableFullscreen !== false;

  const [enableFullscreen, setEnableFullscreen] = useState<boolean>(
    persistedEnableFullscreen,
  );

  useEffect(() => {
    setEnableFullscreen(spot?.enableFullscreen !== false);
  }, [brandingSpotId, spot?.enableFullscreen]);

  const handleToggleEnableFullscreen = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setEnableFullscreen(checked);

      if (!brandingSpotId) {
        return;
      }

      setAutosaveStatus('saving');
      saveSpotEnableFullscreen(checked)
        .then(() => {
          setAutosaveStatus('saved');
        })
        .catch((saveError: Error) => {
          console.error(
            `Failed to save spot fullscreen toggle: ${saveError?.message}`,
            { brandingSpotId },
          );
          // Put the switch back where the record still says it is, so the UI
          // never claims a setting the spot does not have.
          setEnableFullscreen(!checked);
          setAutosaveStatus('error');
        });
    },
    [brandingSpotId, saveSpotEnableFullscreen, setAutosaveStatus],
  );

  const spotLocalizationsKey =
    spot?.brandingSpotId ?? spot?.roomSpotId ?? ueIdentifier ?? null;
  const activeMediaType: 'image' | 'video' = isVideo ? 'video' : 'image';
  const persistedLocalizations = useMemo(
    () => spot?.localizations ?? {},
    [spot?.localizations],
  );

  const { pendingByType, addPendingLanguage, removePendingLanguage } =
    useSpotLocalizations({ spotKey: spotLocalizationsKey });

  const {
    uploadingCodes,
    failures: localizationFailures,
    uploadAsset,
    removeLocalization: removePersistedLocalization,
    clearPreview,
  } = useSpotLocalizationUploader({
    spot: spot ?? null,
    mediaType: activeMediaType,
  });

  const handleAddLocalization = useCallback(
    (code: string) => {
      addPendingLanguage(activeMediaType, code);
    },
    [addPendingLanguage, activeMediaType],
  );

  const handleRemoveLocalization = useCallback(
    async (code: string) => {
      if (code in persistedLocalizations) {
        await removePersistedLocalization(code);
      }
      removePendingLanguage(activeMediaType, code);
    },
    [
      persistedLocalizations,
      removePersistedLocalization,
      removePendingLanguage,
      activeMediaType,
    ],
  );

  const handleUploadLocalizationAsset = useCallback(
    async (code: string, field: 'media' | 'preview', file: File) => {
      await uploadAsset(code, field, file);
      removePendingLanguage(activeMediaType, code);
    },
    [uploadAsset, removePendingLanguage, activeMediaType],
  );

  const handleClearLocalizationPreview = useCallback(
    async (code: string) => {
      await clearPreview(code);
    },
    [clearPreview],
  );

  const openFilePicker = useCallback((input: HTMLInputElement | null) => {
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }, []);

  const handleUploadImage = useCallback(() => {
    openFilePicker(fileInputRef.current);
  }, [openFilePicker, fileInputRef]);

  const handleUploadVideo = useCallback(() => {
    openFilePicker(videoFileInputRef.current);
  }, [openFilePicker, videoFileInputRef]);

  const handleUploadPreview = useCallback(() => {
    openFilePicker(previewImageFileInputRef.current);
  }, [openFilePicker, previewImageFileInputRef]);

  const handleDelete = useCallback(async () => {
    await deleteSpot();
  }, [deleteSpot]);

  // 'media' clears just the uploaded image/video (spot record stays); 'spot'
  // removes the whole spot record. Both share one confirm dialog, worded per mode.
  const [pendingDeleteAction, setPendingDeleteAction] = useState<
    'media' | 'spot' | null
  >(null);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteAction === 'media') {
      await clearSpotMedia();
    } else if (pendingDeleteAction === 'spot') {
      await handleDelete();
    }
    setPendingDeleteAction(null);
  }, [pendingDeleteAction, clearSpotMedia, handleDelete]);

  const handleSelectType = (type: 'IMAGE' | 'VIDEO') => {
    if (isVideoStatic || isBusy) {
      return;
    }
    setSelectedCustomizationSpotType(type);
  };

  const handleOpenVideoLightbox = useCallback(() => {
    if (videoSrc) {
      setLightboxMode('video');
    }
  }, [videoSrc]);

  const handleOpenImageLightbox = useCallback((src: string | null) => {
    if (src) {
      setLightboxMode('image');
    }
  }, []);

  const progressLabel =
    uploadStepMessage ||
    (isApplyingToSurface
      ? translate('editorMode.applyingToSurface')
      : `${uploadProgress}%`);
  const mediaFilename = selectedFile?.name || filename;
  const videoFilename =
    selectedVideoFile?.name || (isVideoSpot ? filename : '');
  const hasVideoSource = !!videoSrc;
  const hasPreviewSource = !!previewThumb;
  const previewStatusLabel = videoPreviewLocalUrl
    ? translate('editorMode.previewSelected')
    : previewImageUrl
    ? translate('editorMode.previewLoaded')
    : translate('editorMode.previewOptional');
  const lightboxImageSrc =
    lightboxMode === 'image'
      ? lightboxOverrideSrc || previewThumb || imageOnlyThumb
      : null;

  const handleShowLocalizationPreview = useCallback(
    (src: string, mode: 'image' | 'video') => {
      setLightboxOverrideSrc(src);
      setLightboxMode(mode);
    },
    [],
  );

  const spotMatchesActiveType = isVideo === isVideoSpot;
  const hasDefaultMedia = spotMatchesActiveType && !!currentUrl;

  const hasUploadError = !!error && !isBusy;
  const videoUploadFailed =
    isVideo && hasUploadError && activeUpload === 'video';
  const previewUploadFailed =
    isVideo && hasUploadError && activeUpload === 'preview';
  const imageUploadFailed =
    !isVideo && hasUploadError && activeUpload === 'image';

  const defaultScopeTag = (
    <Box sx={localizationStyles.scopeTagRow}>
      <Box sx={localizationStyles.scopeTag}>
        <PublicOutlinedIcon sx={{ fontSize: 12 }} />
        {translate('editorMode.defaultScopeTag', {
          count: TOTAL_LOCALIZATION_LANGUAGES + 1,
        })}
      </Box>
      <Typography component="span" sx={localizationStyles.langCode}>
        {DEFAULT_LANGUAGE_CODE.toUpperCase()}
      </Typography>
      {!isDefaultLanguageActive && (
        <Box sx={localizationStyles.activeLangHint}>
          {translate('editorMode.defaultActiveOverrideHint', {
            code: normalizedActiveLanguage.toUpperCase(),
          })}
        </Box>
      )}
    </Box>
  );

  const driftRow = useMemo(
    () =>
      spotDrift.rows.find(
        (row) =>
          normalizeUeIdentifier(row.ueIdentifier) ===
          normalizeUeIdentifier(ueIdentifier),
      ) ?? null,
    [spotDrift, ueIdentifier],
  );
  const isMissing = driftRow?.status === 'missing';
  const isOrphaned = driftRow?.status === 'orphaned';

  const [isDriftMutating, setIsDriftMutating] = useState(false);

  const handleAddToConfiguration = useCallback(async () => {
    if (!currentBrandingId || !isMissing) {
      return;
    }
    setIsDriftMutating(true);
    setAutosaveStatus('saving');
    try {
      await BrandingsService.createBrandingSpot(currentBrandingId, {
        isActive: false,
        type: driftRow?.type ?? 'IMAGE',
        name: ueIdentifier,
        ueIdentifier,
        data: { location: '', url: '' },
      });
      await refreshSpots();
      setAutosaveStatus('saved');
    } catch (addError) {
      console.error(
        `Failed to add spot to configuration: ${(addError as Error).message}`,
        { brandingId: currentBrandingId, ueIdentifier },
      );
      setAutosaveStatus('error');
    } finally {
      setIsDriftMutating(false);
    }
  }, [
    currentBrandingId,
    isMissing,
    driftRow,
    ueIdentifier,
    refreshSpots,
    setAutosaveStatus,
  ]);

  const handleRemoveFromConfiguration = useCallback(async () => {
    const id = spot?.brandingSpotId;
    if (!currentBrandingId || !id) {
      return;
    }
    setIsDriftMutating(true);
    setAutosaveStatus('saving');
    try {
      await BrandingsService.deleteBrandingSpot(currentBrandingId, id);
      await refreshSpots();
      setAutosaveStatus('saved');
      clearSelection();
    } catch (removeError) {
      console.error(
        `Failed to remove spot from configuration: ${
          (removeError as Error).message
        }`,
        { brandingId: currentBrandingId, brandingSpotId: id },
      );
      setAutosaveStatus('error');
    } finally {
      setIsDriftMutating(false);
    }
  }, [
    currentBrandingId,
    spot,
    refreshSpots,
    setAutosaveStatus,
    clearSelection,
  ]);

  const renderDriftPanel = (variant: 'missing' | 'orphaned') => {
    const isNewSpot = variant === 'missing' && !!driftRow?.isNew;
    return (
      <Box sx={driftPanelStyles.wrap}>
        <Box
          sx={
            variant === 'missing'
              ? driftPanelStyles.bannerMissing
              : driftPanelStyles.bannerOrphan
          }
        >
          <ErrorOutlineIcon sx={driftPanelStyles.bannerIcon} />
          <Typography sx={{ fontSize: '12px', lineHeight: 1.45 }}>
            {variant === 'missing'
              ? isNewSpot
                ? translate('editorMode.driftMissingNewBanner')
                : translate('editorMode.driftMissingBanner')
              : translate('editorMode.driftOrphanBanner')}
          </Typography>
        </Box>
        {variant === 'missing' ? (
          <Box
            component="button"
            type="button"
            sx={driftPanelStyles.addButton}
            onClick={handleAddToConfiguration}
            disabled={isDriftMutating}
            data-testid="spot-inspector-add-to-config-btn"
          >
            <AddIcon sx={{ fontSize: 16 }} />
            {translate('editorMode.driftAddToConfig')}
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            sx={driftPanelStyles.removeButton}
            onClick={handleRemoveFromConfiguration}
            disabled={isDriftMutating}
            data-testid="spot-inspector-remove-from-config-btn"
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            {translate('editorMode.driftRemoveFromConfig')}
          </Box>
        )}
      </Box>
    );
  };

  if (isMissing || isOrphaned) {
    return renderDriftPanel(isMissing ? 'missing' : 'orphaned');
  }

  return (
    <Box>
      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.type')}
        </Typography>
        <Box sx={styles.segmented}>
          <Box
            sx={[
              styles.segmentedItem,
              ...(!isVideo ? [styles.segmentedItemActive] : []),
              ...(isVideoStatic || isBusy
                ? [styles.segmentedItemDisabled]
                : []),
            ]}
            onClick={() => handleSelectType('IMAGE')}
            aria-disabled={isVideoStatic || isBusy}
            data-testid="spot-inspector-type-image-btn"
          >
            {translate('editorMode.image')}
          </Box>
          <Box
            sx={[
              styles.segmentedItem,
              ...(isVideo ? [styles.segmentedItemActive] : []),
              ...(isVideoStatic ? [styles.segmentedItemLocked] : []),
              ...(!isVideoStatic && isBusy
                ? [styles.segmentedItemDisabled]
                : []),
            ]}
            onClick={() => handleSelectType('VIDEO')}
            aria-disabled={isBusy}
            data-testid="spot-inspector-type-video-btn"
          >
            {translate('editorMode.video')}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {isVideo
            ? translate('editorMode.videoUploadHeader')
            : translate('editorMode.imageUploadHeader')}
        </Typography>

        {isVideo ? (
          <Box sx={styles.mediaCard}>
            {defaultScopeTag}
            <Box sx={styles.compactRow}>
              <Tooltip
                title={hasVideoSource ? translate('editorMode.viewVideo') : ''}
                placement="bottom"
                enterDelay={400}
              >
                <Box
                  sx={[
                    styles.compactThumb,
                    ...(!hasVideoSource ? [styles.spotThumbPlaceholder] : []),
                    ...(hasVideoSource ? [styles.spotThumbClickable] : []),
                  ]}
                  onClick={handleOpenVideoLightbox}
                  data-testid="spot-inspector-video-thumb"
                >
                  {hasVideoSource ? (
                    <Box sx={styles.spotThumbVideoBadge}>
                      <PlayArrowOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                  ) : (
                    <PlayArrowOutlinedIcon
                      sx={{ fontSize: 22, opacity: 0.7 }}
                    />
                  )}
                  {hasVideoSource && (
                    <Box sx={styles.thumbExpandBadge}>
                      <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>

              <Box sx={styles.compactInfo}>
                <Tooltip
                  title={videoFilename || ''}
                  placement="bottom"
                  enterDelay={400}
                >
                  <Typography sx={styles.compactTitle}>
                    {videoFilename || translate('editorMode.noVideoUploaded')}
                  </Typography>
                </Tooltip>
                <Typography sx={styles.compactSubtitle}>
                  {translate('editorMode.video')}
                  {videoFilename ? ' · WebM' : ''}
                </Typography>
                {videoUploadFailed && (
                  <Tooltip
                    title={error || ''}
                    placement="bottom"
                    enterDelay={200}
                  >
                    <Box sx={styles.previewStatusRow}>
                      <ErrorOutlineIcon
                        sx={{ fontSize: 12, color: '#FF6B6B' }}
                      />
                      <Typography sx={styles.uploadErrorText}>
                        {translate('editorMode.notUploaded')}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>

              <Box sx={styles.compactActions}>
                <Tooltip
                  title={translate('editorMode.uploadVideo')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconSquareButton}
                      onClick={handleUploadVideo}
                      disabled={isBusy}
                      aria-label={translate(
                        'editorMode.uploadVideo',
                        'Upload video',
                      )}
                      data-testid="spot-inspector-video-upload-btn"
                    >
                      <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={translate('editorMode.delete')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={[styles.iconSquareButton, styles.iconDangerButton]}
                      onClick={() => setPendingDeleteAction('media')}
                      disabled={isBusy || !persistedVideoUrl}
                      aria-label={translate('editorMode.delete', 'Delete')}
                      data-testid="spot-inspector-video-delete-btn"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {isBusy && activeUpload === 'video' && (
              <Box
                sx={styles.progressBlock}
                data-testid="spot-inspector-video-upload-progress"
              >
                <LinearProgress
                  variant={
                    isApplyingToSurface ? 'indeterminate' : 'determinate'
                  }
                  value={uploadProgress}
                  sx={styles.compactProgressBar}
                />
                <Box sx={styles.progressTextRow}>
                  <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
                  <Typography sx={styles.conversionText}>
                    {progressLabel}
                  </Typography>
                  {videoUploadCancellable && (
                    <Typography
                      onClick={cancelVideoUpload}
                      sx={styles.cancelUploadButton}
                      data-testid="spot-inspector-cancel-upload-link"
                    >
                      {translate('editorMode.cancel')}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Box sx={styles.compactDivider} />

            <Box sx={styles.compactRow}>
              <Tooltip
                title={
                  hasPreviewSource ? translate('editorMode.viewPreview') : ''
                }
                placement="bottom"
                enterDelay={400}
              >
                <Box
                  sx={[
                    styles.compactThumb,
                    ...(!hasPreviewSource ? [styles.spotThumbPlaceholder] : []),
                    ...(hasPreviewSource ? [styles.spotThumbClickable] : []),
                  ]}
                  style={
                    previewThumb
                      ? { backgroundImage: `url("${previewThumb}")` }
                      : undefined
                  }
                  onClick={() => handleOpenImageLightbox(previewThumb)}
                  data-testid="spot-inspector-preview-thumb"
                >
                  {!hasPreviewSource && (
                    <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                  )}
                  {hasPreviewSource && (
                    <Box sx={styles.thumbExpandBadge}>
                      <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>

              <Box sx={styles.compactInfo}>
                <Typography sx={styles.compactTitle}>
                  {translate('editorMode.previewImage')}
                </Typography>
                <Box sx={styles.previewStatusRow}>
                  {previewUploadFailed ? (
                    <Tooltip
                      title={error || ''}
                      placement="bottom"
                      enterDelay={200}
                    >
                      <Box sx={styles.previewStatusRow}>
                        <ErrorOutlineIcon
                          sx={{ fontSize: 12, color: '#FF6B6B' }}
                        />
                        <Typography sx={styles.uploadErrorText}>
                          {translate('editorMode.notUploaded')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <>
                      {hasPreviewSource && (
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 12, color: '#4CCBA0' }}
                        />
                      )}
                      <Typography
                        sx={[
                          styles.compactSubtitle,
                          ...(hasPreviewSource ? [styles.previewStatusOk] : []),
                        ]}
                      >
                        {previewStatusLabel}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <Box sx={styles.compactActions}>
                <Tooltip
                  title={translate('editorMode.uploadPreview')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconSquareButton}
                      onClick={handleUploadPreview}
                      disabled={isBusy}
                      aria-label={translate(
                        'editorMode.uploadPreview',
                        'Upload preview image',
                      )}
                      data-testid="spot-inspector-preview-upload-btn"
                    >
                      <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                {!!previewImageUrl && (
                  <Tooltip
                    title={translate('editorMode.delete')}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <span>
                      <IconButton
                        size="small"
                        sx={[styles.iconSquareButton, styles.iconDangerButton]}
                        onClick={handleDeletePreview}
                        disabled={isBusy}
                        aria-label={translate('editorMode.delete', 'Delete')}
                        data-testid="spot-inspector-preview-delete-btn"
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {isBusy && activeUpload === 'preview' && (
              <Box
                sx={styles.progressBlock}
                data-testid="spot-inspector-preview-upload-progress"
              >
                <LinearProgress
                  variant={
                    isApplyingToSurface ? 'indeterminate' : 'determinate'
                  }
                  value={uploadProgress}
                  sx={styles.compactProgressBar}
                />
                <Box sx={styles.progressTextRow}>
                  <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
                  <Typography sx={styles.conversionText}>
                    {progressLabel}
                  </Typography>
                </Box>
              </Box>
            )}

            {hasUploadError && (
              <Box sx={styles.uploadErrorBanner}>
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 14,
                    color: '#FF6B6B',
                    flexShrink: 0,
                    mt: '1px',
                  }}
                />
                <Typography sx={styles.uploadErrorBannerText}>
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={styles.mediaCard}>
            {defaultScopeTag}
            <Box sx={styles.compactRow}>
              <Tooltip
                title={imageOnlyThumb ? translate('editorMode.viewLarger') : ''}
                placement="bottom"
                enterDelay={400}
              >
                <Box
                  sx={[
                    styles.compactThumb,
                    ...(!imageOnlyThumb ? [styles.spotThumbPlaceholder] : []),
                    ...(imageOnlyThumb ? [styles.spotThumbClickable] : []),
                  ]}
                  style={
                    imageOnlyThumb
                      ? { backgroundImage: `url("${imageOnlyThumb}")` }
                      : undefined
                  }
                  onClick={() => handleOpenImageLightbox(imageOnlyThumb)}
                  data-testid="spot-inspector-image-thumb"
                >
                  {!imageOnlyThumb && (
                    <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                  )}
                  {!!imageOnlyThumb && (
                    <Box sx={styles.thumbExpandBadge}>
                      <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>

              <Box sx={styles.compactInfo}>
                <Tooltip
                  title={mediaFilename || ''}
                  placement="bottom"
                  enterDelay={400}
                >
                  <Typography sx={styles.compactTitle}>
                    {mediaFilename || translate('editorMode.noMedia')}
                  </Typography>
                </Tooltip>
                <Typography sx={styles.compactSubtitle}>
                  {translate('editorMode.image')}
                </Typography>
                {imageUploadFailed && (
                  <Tooltip
                    title={error || ''}
                    placement="bottom"
                    enterDelay={200}
                  >
                    <Box sx={styles.previewStatusRow}>
                      <ErrorOutlineIcon
                        sx={{ fontSize: 12, color: '#FF6B6B' }}
                      />
                      <Typography sx={styles.uploadErrorText}>
                        {translate('editorMode.notUploaded')}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {isBusy && (
                  <Box sx={styles.progressTextRow}>
                    <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
                    <Typography sx={styles.conversionText}>
                      {progressLabel}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={styles.compactActions}>
                <Tooltip
                  title={translate('editorMode.uploadImage')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconSquareButton}
                      onClick={handleUploadImage}
                      disabled={isBusy}
                      aria-label={translate(
                        'editorMode.uploadImage',
                        'Upload image',
                      )}
                      data-testid="spot-inspector-image-upload-btn"
                    >
                      <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={translate('editorMode.delete')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={[styles.iconSquareButton, styles.iconDangerButton]}
                      onClick={() => setPendingDeleteAction('media')}
                      disabled={isBusy || !imageThumbUrl}
                      aria-label={translate('editorMode.delete', 'Delete')}
                      data-testid="spot-inspector-image-delete-btn"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {isBusy && (
              <Box
                sx={styles.progressBlock}
                data-testid="spot-inspector-image-upload-progress"
              >
                <LinearProgress
                  variant={
                    isApplyingToSurface ? 'indeterminate' : 'determinate'
                  }
                  value={uploadProgress}
                  sx={styles.compactProgressBar}
                />
              </Box>
            )}

            {hasUploadError && (
              <Box sx={styles.uploadErrorBanner}>
                <ErrorOutlineIcon
                  sx={{
                    fontSize: 14,
                    color: '#FF6B6B',
                    flexShrink: 0,
                    mt: '1px',
                  }}
                />
                <Typography sx={styles.uploadErrorBannerText}>
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {isVideo && (
          <Typography sx={{ ...styles.spotFilenameMeta, padding: '0 4px' }}>
            {translate('editorMode.videoFormatsHint')}
          </Typography>
        )}
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.localizationOverridesHeader')}
        </Typography>
        <LocalizationSection
          mediaType={activeMediaType}
          persistedOverrides={persistedLocalizations}
          pendingCodes={pendingByType[activeMediaType]}
          uploadingCodes={uploadingCodes}
          failures={localizationFailures}
          hasDefaultMedia={hasDefaultMedia}
          activeLanguageCode={normalizedActiveLanguage}
          onAddLanguage={handleAddLocalization}
          onRemoveLanguage={handleRemoveLocalization}
          onUploadAsset={handleUploadLocalizationAsset}
          onClearPreview={handleClearLocalizationPreview}
          onShowPreview={handleShowLocalizationPreview}
        />
      </Box>

      {showDistanceSection && (
        <Box sx={styles.section}>
          <Box sx={styles.distanceHeader}>
            <Typography sx={styles.fieldLabel}>
              {translate('editorMode.autoPlayDistance')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isDistanceInfinite}
                  onChange={handleToggleInfiniteDistance}
                  inputProps={
                    {
                      'data-testid': 'spot-inspector-infinite-distance-switch',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={
                <Box sx={styles.infiniteLabel}>
                  <Typography component="span" sx={styles.infiniteSymbol}>
                    ∞
                  </Typography>
                  <Typography component="span" sx={styles.infiniteText}>
                    {translate('editorMode.distanceInfinite')}
                  </Typography>
                </Box>
              }
              sx={styles.distanceInfiniteToggle}
            />
          </Box>
          {!isDistanceInfinite && (
            <Box sx={styles.distanceSliderRow}>
              <Slider
                size="small"
                min={DISTANCE_MIN_METERS}
                max={DISTANCE_MAX_METERS}
                step={1}
                value={distanceMeters}
                onChange={handleDistanceChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) =>
                  translate('editorMode.distanceUnitM', {
                    value: v,
                  })
                }
                sx={styles.distanceSlider}
                slotProps={
                  {
                    input: {
                      'data-testid': 'spot-inspector-distance-slider',
                    },
                  } as SliderProps['slotProps']
                }
              />
              <Typography sx={styles.distanceValue}>
                {translate('editorMode.distanceUnitM', {
                  value: distanceMeters,
                })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showAudioSection && (
        <Box sx={styles.section}>
          <Box sx={styles.distanceHeader}>
            <Typography sx={styles.fieldLabel}>
              {translate('editorMode.spotAudio')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isMuted}
                  onChange={handleToggleMute}
                  inputProps={
                    {
                      'data-testid': 'spot-inspector-mute-switch',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={
                <Box sx={styles.infiniteLabel}>
                  {isMuted ? (
                    <VolumeOffOutlinedIcon sx={{ fontSize: 15 }} />
                  ) : (
                    <VolumeUpOutlinedIcon sx={{ fontSize: 15 }} />
                  )}
                  <Typography component="span" sx={styles.infiniteText}>
                    {translate('editorMode.audioMute')}
                  </Typography>
                </Box>
              }
              sx={styles.distanceInfiniteToggle}
            />
          </Box>
          {!isMuted && (
            <Box sx={styles.distanceSliderRow}>
              <Slider
                size="small"
                min={0}
                max={1}
                step={AUDIO_VOLUME_STEP}
                value={volume}
                onChange={handleVolumeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) =>
                  translate('editorMode.volumePercent', {
                    value: toVolumePercent(v),
                  })
                }
                sx={styles.distanceSlider}
                slotProps={
                  {
                    input: {
                      'data-testid': 'spot-inspector-volume-slider',
                    },
                  } as SliderProps['slotProps']
                }
              />
              <Typography sx={styles.distanceValue}>
                {translate('editorMode.volumePercent', {
                  value: toVolumePercent(volume),
                })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showFullscreenSection && (
        <Box sx={styles.section}>
          <Box sx={styles.distanceHeader}>
            <Typography sx={styles.fieldLabel}>
              {translate('editorMode.spotInteraction')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={enableFullscreen}
                  onChange={handleToggleEnableFullscreen}
                  inputProps={
                    {
                      'data-testid': 'spot-inspector-enable-fullscreen-switch',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={
                <Box sx={styles.infiniteLabel}>
                  <OpenInFullOutlinedIcon sx={{ fontSize: 15 }} />
                  <Typography component="span" sx={styles.infiniteText}>
                    {translate('editorMode.enableFullscreenView')}
                  </Typography>
                </Box>
              }
              sx={styles.distanceInfiniteToggle}
            />
          </Box>
          <Typography sx={{ ...styles.spotFilenameMeta, padding: '0 4px' }}>
            {translate('editorMode.enableFullscreenViewHint')}
          </Typography>
        </Box>
      )}

      <Box sx={styles.section}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setPendingDeleteAction('spot')}
          disabled={isBusy}
          sx={deleteSpotButtonStyles}
          data-testid="spot-inspector-delete-spot-btn"
        >
          {translate('editorMode.deleteSpot', 'Delete spot')}
        </Button>
      </Box>

      <Dialog
        open={pendingDeleteAction !== null}
        onClose={() => setPendingDeleteAction(null)}
        data-testid="spot-delete-dialog"
        PaperProps={{
          sx: {
            background:
              'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
            borderRadius: '16px',
            color: '#e2e4ed',
            '& .MuiDialogTitle-root': { color: '#e2e4ed' },
            '& .MuiDialogContentText-root': { color: '#e2e4ed' },
            '& .MuiButton-text': { color: '#e2e4ed' },
            '& .MuiButton-textError': { color: '#f06d6d' },
          },
        }}
      >
        <DialogTitle>
          {pendingDeleteAction === 'media'
            ? translate('editorMode.deleteSpotMedia', 'Delete spot media')
            : translate('editorMode.deleteSpot', 'Delete spot')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeleteAction === 'media'
              ? translate(
                  'editorMode.deleteSpotMediaConfirm',
                  'This permanently deletes the uploaded image or video from this spot, in both the configuration and the experience. The spot itself is not removed and you can upload new media at any time.',
                )
              : translate(
                  'editorMode.deleteSpotConfirm',
                  'This removes the spot and its media from the configuration. This cannot be undone.',
                )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDeleteAction(null)}
            disabled={isBusy}
            data-testid="spot-delete-dialog-cancel-btn"
          >
            {translate('common.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={isBusy}
            data-testid="spot-delete-dialog-confirm-btn"
          >
            {translate('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={lightboxMode !== null}
        onClose={() => setLightboxMode(null)}
        sx={styles.lightboxBackdrop}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
          onClick={() => setLightboxMode(null)}
        >
          {lightboxMode === 'video' && (lightboxOverrideSrc || videoSrc) ? (
            <Box
              component="video"
              src={lightboxOverrideSrc || videoSrc || ''}
              controls
              autoPlay
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              data-testid="spot-inspector-lightbox-media"
            />
          ) : lightboxImageSrc ? (
            <Box
              component="img"
              src={lightboxImageSrc}
              alt={filename || ueIdentifier}
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              data-testid="spot-inspector-lightbox-media"
            />
          ) : null}
          <IconButton
            onClick={() => setLightboxMode(null)}
            sx={styles.lightboxClose}
            size="small"
            aria-label={translate('editorMode.close', 'Close')}
            data-testid="spot-inspector-lightbox-close-btn"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: 'none' }}
        onChange={(e) => handleImageFiles(e.target.files)}
        data-testid="spot-inspector-image-file-input"
      />
      <input
        ref={videoFileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        style={{ display: 'none' }}
        onChange={(e) => handleVideoFileSelect(e.target.files)}
        data-testid="spot-inspector-video-file-input"
      />
      <input
        ref={previewImageFileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: 'none' }}
        onChange={(e) => handlePreviewImageFileSelect(e.target.files)}
        data-testid="spot-inspector-preview-file-input"
      />
    </Box>
  );
};

export default SpotInspector;
